import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ulid } from 'ulid';
import { Storage } from 'aws-amplify';

import Context from '../../../store/context';
import useFetch from '../http/useFetch';
import { setLoading } from '../../../store/slices/UI';
import { paths } from '../../../routes';
import apiMethods from '../../../apis';

const categoryAPI = {
  purchase: apiMethods.createPurchase,
  sale: apiMethods.createPurchase
};

export const getS3PathGivenCategory = (businessId, category, extension) => {
  const id = ulid();
  const path = `businesses/${businessId}/documents/${category}/${id}.${extension}`;
  return { id, path };
};

const maximunFiles = 25;
export const acceptedExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
export const acceptedExtensionsDocuments = ['pdf', 'docx'];

const useUpload = category => {
  // State
  const [loadedFiles, setLoadedFiles] = useState([]);
  const totalFiles = loadedFiles.length;
  const showLoadedFiles = totalFiles > 0;

  // Global state
  const { businessId } = useSelector(state => state.userData);

  // UI
  const {
    modal: { showError: showErrorModal },
    toast: { showError, showSuccess }
  } = useContext(Context);

  const { sendRequest: createBatchDocuments, isSuccess, isError, errorMessage } = useFetch(categoryAPI[category]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sendFilesHandler = async () => {
    const moreThanZeroFile = totalFiles > 0;
    if (!moreThanZeroFile) {
      showError('No has cargado archivos');
      return;
    }
    dispatch(setLoading(true));
    const loadedDocuments = [];
    const errorDocuments = [];
    for (const [idx, file] of loadedFiles.entries()) {
      const { dropFile, extension } = file;
      const { id, path } = getS3PathGivenCategory(businessId, category, extension);
      const fileData = { id, file: path };
      try {
        await Storage.put(path, dropFile, {
          progressCallback({ loaded, total }) {
            setLoadedFiles(loadedFiles => {
              let auxLoadedFiles = [...loadedFiles];
              auxLoadedFiles[idx] = { ...auxLoadedFiles[idx], loaded, total };
              return auxLoadedFiles;
            });
          }
        });
        loadedDocuments.push(fileData);
      } catch (error) {
        errorDocuments.push(fileData);
      }
    }
    dispatch(setLoading(false));
    if (loadedDocuments.length > 0) {
      createBatchDocuments(businessId, loadedDocuments);
    } else {
      showError('No fue posible cargar los archivos');
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const successCallback = () => navigate(paths.documents);
      showSuccess('Documentos correctamente cargados', successCallback);
    } else if (isError) {
      showErrorModal({ title: 'Cargar archivos', description: errorMessage });
    }
  }, [isError, isSuccess, navigate, showErrorModal, showSuccess, errorMessage]);

  const droppedFilesHandler = files => {
    const lessThanMaximum = files.length <= maximunFiles;
    if (!lessThanMaximum) {
      showError(`El número máximo de archivos es ${maximunFiles}`);
      return;
    }

    const allExtensionsCorrect = files.reduce((prev, { type }) => prev && acceptedExtensions.includes(type.split('/')[1]), true);
    if (!allExtensionsCorrect) {
      showError('La extensión de 1 o más archivos es incorrecta');
      return;
    }

    setLoadedFiles(files);
  };

  return { droppedFilesHandler, sendFilesHandler, showLoadedFiles, loadedFiles, setLoadedFiles };
};

export default useUpload;
